import { Redirect, router, useLocalSearchParams } from 'expo-router';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';

import { Button } from '@/components/Button';
import { Heading } from '@/components/Heading';
import { Overline } from '@/components/Overline';
import { PinInput, type PinInputHandle } from '@/components/PinInput';
import { Screen } from '@/components/Screen';
import { Text } from '@/components/Text';
import { colors, spacing } from '@/constants/theme';
import { campaignCopy } from '@/lib/campaignCopy';
import { GIFTING_ROUTES, giftingRouteFor } from '@/lib/giftingRoute';
import { accountMergeService } from '@/services/accountMerge';
import { isApiError } from '@/services/errors';
import { authStore } from '@/stores/auth.store';

const RESEND_COOLDOWN_S = 60;

/**
 * The email-collision second door: the completion form's email belongs to an
 * existing account, a code was just mailed to it, and typing that code here
 * proves the inbox. The server then merges — this phone attaches to the
 * account that owns the email, and THIS session becomes that account
 * (authStore.mergeIntoEmailAccount swaps tokens; the old account is gone).
 *
 * Reached only by explicit navigation from the completion form (never from
 * gifting_next_step), so the guard is minimal: an email param and a signed-in
 * user, or back to where the funnel would have put them.
 */
const LinkAccountScreen = observer(() => {
  const { email, masked } = useLocalSearchParams<{ email?: string; masked?: string }>();
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [secondsRemaining, setSecondsRemaining] = useState(RESEND_COOLDOWN_S);
  const [resending, setResending] = useState(false);
  const pinRef = useRef<PinInputHandle>(null);

  useEffect(() => {
    if (secondsRemaining <= 0) return;
    const id = setInterval(() => setSecondsRemaining((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [secondsRemaining]);

  if (!authStore.hydrated) {
    return (
      <View style={styles.loading}>
        <ActivityIndicator color={colors.deepGreen} />
      </View>
    );
  }

  if (!authStore.user || !email) {
    return <Redirect href={giftingRouteFor(authStore.user) ?? GIFTING_ROUTES.landing} />;
  }

  async function handleComplete(value: string) {
    // PinInput fires onComplete on every 6-digit value, so a paste-then-edit
    // can land here twice while the first confirm is in flight.
    if (!email || submitting) return;
    setSubmitting(true);
    setErrorCode(null);
    setErrorMessage(null);
    try {
      await authStore.mergeIntoEmailAccount(email, value);
      // We are now the merged account; the server's payload says where the
      // funnel continues (usually straight to the gift grid).
      router.replace(giftingRouteFor(authStore.user) ?? GIFTING_ROUTES.landing);
    } catch (err) {
      setPin('');
      pinRef.current?.focus();
      if (isApiError(err)) {
        setErrorCode(err.code);
        setErrorMessage(err.message);
      } else {
        setErrorMessage('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend() {
    if (!email || secondsRemaining > 0 || resending) return;
    setResending(true);
    setErrorCode(null);
    setErrorMessage(null);
    try {
      await accountMergeService.request(email);
      setSecondsRemaining(RESEND_COOLDOWN_S);
    } catch (err) {
      setErrorMessage(isApiError(err) ? err.message : 'Could not resend. Please try again.');
    } finally {
      setResending(false);
    }
  }

  const lockedOut = errorCode === 'pin_locked';
  // Merge-specific dead ends (the code was right but the merge itself is
  // impossible) — the email door still works for these accounts.
  const needsEmailSignIn = errorCode === 'phone_conflict' || errorCode === 'merge_unsupported';

  return (
    <Screen width="column">
      <View style={styles.body}>
        <Overline>{campaignCopy(authStore.user?.gifting_copy).name}</Overline>
        <Heading size="4xl" style={styles.headline}>
          Prove it&apos;s you.
        </Heading>
        <Text color="textMuted" style={styles.copy}>
          We sent a 6-digit code to {masked ?? 'your email'}. Enter it and we&apos;ll link your
          phone number to that account — your gift comes with you.
        </Text>

        <View style={styles.pin}>
          <PinInput
            ref={pinRef}
            value={pin}
            onChange={setPin}
            onComplete={(value) => void handleComplete(value)}
            hasError={Boolean(errorCode)}
            autoFocus
            editable={!submitting && !lockedOut}
          />
        </View>

        {submitting && <ActivityIndicator color={colors.deepGreen} style={styles.spinner} />}

        {errorMessage && (
          <Text variant="caption" color="danger" style={styles.error}>
            {errorMessage}
          </Text>
        )}

        {needsEmailSignIn ? (
          <Button
            label="Sign in with that email instead"
            variant="outline"
            onPress={() =>
              router.replace({
                pathname: '/(auth)/login',
                params: { prefill_contact: email, redirect: GIFTING_ROUTES.complete },
              })
            }
            style={styles.action}
          />
        ) : (
          <Button
            label={
              resending
                ? 'Sending…'
                : secondsRemaining > 0
                  ? `Resend code in ${secondsRemaining}s`
                  : 'Resend code'
            }
            variant="outline"
            onPress={() => void handleResend()}
            disabled={secondsRemaining > 0 || resending}
            style={styles.action}
          />
        )}
      </View>
    </Screen>
  );
});

export default LinkAccountScreen;

const styles = StyleSheet.create({
  loading: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background,
  },
  body: {
    paddingTop: spacing.sm,
  },
  headline: {
    marginTop: spacing.md,
  },
  copy: {
    marginTop: spacing.base,
  },
  pin: {
    marginTop: spacing.lg,
  },
  spinner: {
    marginTop: spacing.base,
  },
  error: {
    marginTop: spacing.base,
  },
  action: {
    marginTop: spacing.lg,
  },
});
